import { useMemo } from "react";
import { TProxyPlanKey } from "./useProxyPlanKey";
import { typedSplit } from "@/utils/textUtils";
import {
  CustomizationSectionType,
  CustomizationSourceType,
} from "@/components/subscription/customize/hooks/useSections";

import { PlanIdTypeSafe, PlanType } from "@/types/PlanTypes";
import { prepareObjectForQuery } from "@/hooks/urlState";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import { pickBy } from "lodash";
import {
  DEFAULT_CUSTOMIZE_PATHNAME,
  SHOW_REPLACE_REFRESH_ROTATE_QUERY_PARAM_KEY,
} from "@/utils/formHref";
import usePlan from "@/hooks/usePlan";
import { useSession } from "@/security/RouteGuards";
import type { RouteTarget } from "@/routing/routeTarget";

type ShowCustomizeSectionsType = CustomizationSectionType[];

export type ReplaceRefreshRotatesSectionType =
  | "autorefresh"
  | "replaceAll"
  | "replace";

export type UseCustomizeArgs = {
  showCustomizeSections?: ShowCustomizeSectionsType | null;
  showRRRTab?:
    | ReplaceRefreshRotatesSectionType
    | ReplaceRefreshRotatesSectionType[]
    | null;
  source?: CustomizationSourceType;
  recommendOnDemandRefreshes?: string | null;
  recommendReplace?: string | null;
  purchaseInitiatorPath?: string | null;
  menu?: string | null; //Used only by ResidentialProxyBanner.
  // In this case it means that we do not have a plan for this tab type yet
  existingPlanId?: PlanIdTypeSafe;
  proxyPlan?: TProxyPlanKey;
  /**
   * This is useful with multipe plans when you press "Buy Again", as cancelled plans won't load
   * so we don't pass in existingPlanId, but we still do not want to set defaults from
   * session default plan
   */
  skipDefaults?: boolean;
  overrides?: {
    isHighConcurrency?: boolean;
    isHighPriorityNetwork?: boolean;
    isUnlimitedIpAuthorizations?: boolean;
    proxyCount?: number;
    customProxyCount?: number;
    bandwidth?: number;
    proxyReplacements?: number;
  };
};

function applyOverride(
  overrideValue: boolean | undefined,
  planValue: boolean | undefined,
  defaultValue: boolean
) {
  if (overrideValue !== undefined) {
    return overrideValue;
  }
  if (planValue !== undefined) {
    return planValue;
  }
  return defaultValue;
}

export default function useCustomizeQuery({
  existingPlanId,
  proxyPlan: _proxyPlan,
  showCustomizeSections,
  showRRRTab,
  source,
  recommendOnDemandRefreshes,
  purchaseInitiatorPath,
  recommendReplace,
  overrides,
  skipDefaults,
}: UseCustomizeArgs) {
  const { data: plan, isLoading } = usePlan(existingPlanId || "default");
  const planId = existingPlanId || "default";
  const { subscription } = useSession();
  // Seed the billing term from the current subscription like every other plan
  // field — a hardcoded monthly default silently converted yearly
  // subscriptions on checkout (R-1941).
  const isYearly = subscription?.term === "yearly";

  const query = useMemo(() => {
    const defaultsFrom: Partial<PlanType> | undefined =
      // If we don't have existing plan then we shouldn't use the defaults from the "default" plan as it may not match
      skipDefaults || !existingPlanId ? {} : plan || {};
    const {
      proxy_countries,
      subusers_total,
      is_high_concurrency,
      is_high_priority_network,
      is_unlimited_ip_authorizations,
      high_quality_ips_only,
      required_site_checks,
      on_demand_refreshes_total,
      proxy_replacements_total,
      automatic_refresh_frequency,
      proxy_count,
      bandwidth_limit,
      // If user has a free plan, then do not transfer those values over
    } = plan?.proxy_type === "free" ? {} : defaultsFrom;
    const defaultCountries = proxy_countries as Record<string, number>;

    const proxyPlan =
      _proxyPlan ||
      getProxyPlanKey({
        proxy_type: plan?.proxy_type || "default",
        proxy_subtype: plan?.proxy_subtype || "free",
        proxy_count: plan?.proxy_count,
      });

    const _siteChecks =
      proxyPlan === "shared_verified"
        ? required_site_checks || ["google_search_without_captcha"]
        : [];

    const [proxyType, proxySubtype] = typedSplit(proxyPlan, "_");

    const params = prepareObjectForQuery({
      planId: planId,
      proxyType,
      proxySubtype,
      proxyCount: overrides?.proxyCount ?? proxy_count,
      customProxyCount: overrides?.customProxyCount ?? proxy_count,
      bandwidth: overrides?.bandwidth ?? bandwidth_limit,
      autoRefreshFrequency: automatic_refresh_frequency,
      onDemandRefreshes: on_demand_refreshes_total,
      proxyReplacements:
        overrides?.proxyReplacements ?? proxy_replacements_total,
      countries: defaultCountries || {},
      isCustomSubuserCount: false,
      siteChecks: _siteChecks,
      customSubuserCount: 0,
      subuserCount: Number(subusers_total) >= 3 ? subusers_total : 3,
      isUnlimitedIpAuthorizations: applyOverride(
        overrides?.isUnlimitedIpAuthorizations,
        is_unlimited_ip_authorizations,
        false
      ),
      isHighConcurrency: applyOverride(
        overrides?.isHighConcurrency,
        is_high_concurrency,
        false
      ),
      isHighPriorityNetwork: applyOverride(
        overrides?.isHighPriorityNetwork,
        is_high_priority_network,
        false
      ),
      isHighIpReputation: high_quality_ips_only || false,
      isYearly,
    });

    const cleanedObject = pickBy(
      {
        ["show_customization_sections"]:
          showCustomizeSections && showCustomizeSections.length > 0
            ? showCustomizeSections.join(",")
            : undefined,
        [SHOW_REPLACE_REFRESH_ROTATE_QUERY_PARAM_KEY]: showRRRTab,
        source: source ? JSON.stringify(source) : undefined,
        ["recommend_on_demand_refreshes"]: recommendOnDemandRefreshes,
        ["purchase_initiator_path"]: purchaseInitiatorPath,
        ["recommend_replace"]: recommendReplace,
        ...params,
      },
      (v) => v !== undefined && v !== null
    );

    return cleanedObject;
  }, [
    _proxyPlan,
    showCustomizeSections,
    showRRRTab,
    source,
    recommendOnDemandRefreshes,
    purchaseInitiatorPath,
    recommendReplace,
    planId,
    plan,
    skipDefaults,
    existingPlanId,
    overrides,
    isYearly,
  ]);

  const linkMemoized: RouteTarget = useMemo(
    () => ({
      to: DEFAULT_CUSTOMIZE_PATHNAME,
      search: query,
    }),
    [query]
  );

  return {
    link: linkMemoized,
    isLoading,
  };
}
